/**
 * db_manager.ts
 * Centralized service for Cloudflare D1 operations.
 * Acts as a bridge between the frontend and the Pages Functions at /api/db.
 */
import { supabase } from './supabaseClient';

interface DBRequest {
    action: 'select' | 'upsert' | 'insert' | 'delete' | 'select_public' | 'delete_filtered' | 'count' | 'update_filtered' | 'toggle_interaction' | 'increment_streak' | 'end_voice_session' | 'get_active_voice_total' | 'get_active_voice_chart' | 'get_flashcards_chart_stats' | 'get_filtered_stats' | 'get_daily_logs' | 'increment_stat' | 'sync_achievements';
    table: string;
    data?: any;
    id?: string | number;
    user_id?: string;
    filters?: Record<string, any>;
    join?: 'friendships' | 'book_club_members' | 'club_comments' | 'profiles' | 'post_comments' | 'posts_interactions' | 'flashcards_progress' | 'notifications_profiles';
}

// --- Session Cache ---
// Avoids calling supabase.auth.getSession() on every single request,
// which triggers the Navigator LockManager warning and floods the console.
let cachedSession: { userId: string; token: string } | null = null;
let sessionCacheExpiry = 0;
const SESSION_CACHE_TTL = 4 * 60 * 1000; // 4 minutes

// Listen to auth state changes to update the cache proactively
supabase.auth.onAuthStateChange((_event, session) => {
    if (session) {
        cachedSession = { userId: session.user.id, token: session.access_token };
        sessionCacheExpiry = Date.now() + SESSION_CACHE_TTL;
    } else {
        cachedSession = null;
        sessionCacheExpiry = 0;
    }
});

const getSessionCached = async (): Promise<{ userId: string; token: string } | null> => {
    if (cachedSession && Date.now() < sessionCacheExpiry) {
        return cachedSession;
    }
    try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error || !session) {
            cachedSession = null;
            return null;
        }
        cachedSession = { userId: session.user.id, token: session.access_token };
        sessionCacheExpiry = Date.now() + SESSION_CACHE_TTL;
        return cachedSession;
    } catch {
        return null;
    }
};
// --- End Session Cache ---

// --- Global Throttling ---
let isGlobalThrottled = false;
let throttleUntil = 0;

const checkThrottle = async () => {
    if (isGlobalThrottled && Date.now() < throttleUntil) {
        const waitTime = throttleUntil - Date.now();
        console.warn(`[DBManager] Global Throttling active. Waiting ${waitTime}ms...`);
        await new Promise(res => setTimeout(res, waitTime));
    }
};
// --- End Global Throttling ---

let requestQueue: Promise<any> = Promise.resolve();

export const dbManager = {
    /**
     * Primary method to interact with the D1 API
     */
    async execute(request: DBRequest) {
        // Basic type conversion for SQLite:
        if (request.data) {
            Object.keys(request.data).forEach(key => {
                const value = request.data[key];
                if (value !== null && typeof value === 'object') {
                    request.data[key] = JSON.stringify(value);
                }
                if (typeof value === 'boolean') {
                    request.data[key] = value ? 1 : 0;
                }
            });
        }

        if (request.filters) {
            Object.keys(request.filters).forEach(key => {
                const value = request.filters![key];
                // Ensure primitive types are handled for filters too
                if (typeof value === 'boolean') {
                    request.filters![key] = value ? 1 : 0;
                }
            });
        }

        // Serial Queue Execution to avoid parallel request spikes & 429 errors
        const executePromise = new Promise(async (resolve, reject) => {
            try {
                await requestQueue;
            } catch (e) {
                // Ignore previous request errors
            }

            try {
                // If it's a write action, throttle/debounce to avoid 429
                const isWrite = ['insert', 'upsert', 'update', 'delete', 'deleteMatch'].includes(request.action as string) || 
                                String(request.action).includes('save') || 
                                ['logs_unique_words', 'quiz_progress', 'quiz_wrong_answers'].includes(request.table);
                if (isWrite) {
                    await new Promise(res => setTimeout(res, 80)); // 80ms delay between write transactions
                }
                const res = await dbManager.executeInternal(request);
                resolve(res);
            } catch (err) {
                reject(err);
            }
        });

        requestQueue = executePromise;
        return executePromise;
    },

    async executeInternal(request: DBRequest) {
        try {
            // Get user_id dynamically if not provided
            let effectiveUserId = request.user_id;
            let accessToken = '';

            const sessionData = await getSessionCached();
            if (sessionData) {
                effectiveUserId = sessionData.userId;
                accessToken = sessionData.token;
            }

            if (!effectiveUserId && request.action !== 'select_public') {
                throw new Error("Sessão expirada. Por favor, faça login novamente.");
            }

            // AÇÃO: Aguardar se houver throttling global ativo
            await checkThrottle();

            // AÇÃO: Exponential Backoff (Anti-DoS) para erro 429
            const MAX_RETRIES = 3;
            const DELAYS = [2000, 4000, 8000];
            let retryCount = 0;

            while (retryCount <= MAX_RETRIES) {
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 30000); // 30s timeout

                try {
                    const response = await fetch('/api/db', {
                        method: 'POST',
                        keepalive: true,
                        headers: {
                            'Content-Type': 'application/json',
                            'x-user-id': effectiveUserId || '',
                            'Authorization': accessToken ? `Bearer ${accessToken}` : ''
                        },
                        body: JSON.stringify(request),
                        signal: controller.signal
                    });

                    clearTimeout(timeoutId);

                    // Se erro 429, aguarda exponencialmente e tenta novamente
                    if (response.status === 429 && retryCount < MAX_RETRIES) {
                        const delay = DELAYS[retryCount];
                        console.warn(`[DBManager] 429 Too Many Requests. Retentativa em ${delay}ms... (${retryCount + 1}/${MAX_RETRIES})`);
                        
                        // Ativa throttling global para não bombardear com outras requisições
                        isGlobalThrottled = true;
                        throttleUntil = Date.now() + delay;

                        await new Promise(res => setTimeout(res, delay));
                        retryCount++;
                        continue;
                    }

                    const contentType = response.headers.get('content-type');
                    const isJson = contentType && contentType.includes('application/json');

                    if (!response.ok) {
                        let errorMsg = `HTTP ${response.status} Error`;
                        if (isJson) {
                            const errorData = await response.json();
                            errorMsg = errorData.error || errorMsg;
                        } else {
                            const text = await response.text();
                            if (text.includes("cloudflare") || text.includes("CLOUDFLARE_CHALLENGE")) {
                                window.dispatchEvent(new CustomEvent('libritui-cloudflare-challenge'));
                                throw new Error("Ação bloqueada pela Cloudflare. Validando...");
                            }
                            try {
                                const parsedErr = JSON.parse(text);
                                errorMsg = parsedErr?.error || parsedErr?.details || `Status ${response.status}: Erro no servidor.`;
                            } catch {
                                errorMsg = `Status ${response.status}: Erro no servidor - ${text.substring(0, 100)}`;
                            }
                        }
                        const err = new Error(errorMsg);
                        // HTTP 4xx or script execution runtime errors are non-retryable
                        if (
                            (response.status >= 400 && response.status < 500) ||
                            errorMsg.includes('is not defined') ||
                            errorMsg.includes('ReferenceError') ||
                            errorMsg.includes('TypeError') ||
                            errorMsg.includes('SyntaxError')
                        ) {
                            (err as any).isClientError = true;
                        }
                        throw err;
                    }

                    const text = await response.text();

                    const isWrite = ['insert', 'upsert', 'update', 'delete', 'deleteMatch'].includes(request.action);
                    if (isWrite && ['flashcards', 'flashcards_imported', 'flashcards_feedback', 'decks'].includes(request.table)) {
                        const userId = request.user_id || effectiveUserId;
                        if (userId) {
                            localStorage.removeItem(`libritui_cache_d1_flashcards_${userId}`);
                            localStorage.removeItem(`libritui_cache_d1_flashcards_imported_${userId}`);
                            localStorage.removeItem(`libritui_cache_d1_decks_${userId}`);
                            localStorage.removeItem(`libritui_cache_all_decks_${userId}`);
                        }
                    }

                    return isJson ? JSON.parse(text) : text;

                } catch (err: any) {
                    // Erros de rede ou aborto: aguarda e tenta de novo se houver retentativas (NÃO tenta de novo para 4xx ou bugs de execução)
                    if (
                        retryCount >= MAX_RETRIES || 
                        (err instanceof Error && err.name === 'AbortError') || 
                        err?.isClientError ||
                        (err?.message && (
                            err.message.includes('is not defined') ||
                            err.message.includes('ReferenceError') ||
                            err.message.includes('TypeError') ||
                            err.message.includes('SyntaxError')
                        ))
                    ) {
                        throw err;
                    }
                    if (err instanceof Error && (
                        err.message.includes('no such table') ||
                        err.message.includes('Table not allowed') ||
                        err.message.includes('SQLITE_CONSTRAINT')
                    )) {
                        if (request.table === 'logs_unique_words' || request.action === ('record_unique_words' as any)) {
                            console.log('[DBManager] SQLITE_CONSTRAINT on logs_unique_words handled gracefully.');
                            return { success: true, count: 0 };
                        }
                        console.warn('[DBManager] Breaking retry loop for table or constraint error:', err.message);
                        throw err;
                    }
                    const retryDelay = 2000;
                    console.warn(`[DBManager] Network error, retrying in ${retryDelay}ms...`, err.message);
                    await new Promise(res => setTimeout(res, retryDelay));
                    retryCount++;
                }
            }
        } catch (error: any) {
            if (error?.name !== 'AbortError') {
                console.warn(`[DBManager] Managed connection issue in ${request.action} on ${request.table}:`, error);
            }
            throw error;
        }
    },

    /**
     * Generic helpers
     */
    from(table: string, userId?: string) {
        return {
            select: async (filters?: Record<string, any>, options?: { limit?: number; offset?: number; orderBy?: string }, join?: DBRequest['join']) => {
                return dbManager.execute({
                    action: userId ? 'select' : 'select_public',
                    table,
                    user_id: userId,
                    filters,
                    join,
                    data: options // Pass extra options like limit through 'data' which backend reads
                });
            },
            upsert: async (data: any) => {
                return dbManager.execute({
                    action: 'upsert',
                    table,
                    data,
                    user_id: userId
                });
            },
            insert: async (data: any) => {
                return dbManager.execute({
                    action: 'insert',
                    table,
                    data,
                    user_id: userId
                });
            },
            bulkInsert: async (items: any[]) => {
                return dbManager.execute({
                    action: 'bulk_insert' as any,
                    table,
                    data: { items },
                    user_id: userId
                });
            },
            delete: async (id: string | number) => {
                return dbManager.execute({
                    action: 'delete',
                    table,
                    id,
                    user_id: userId
                });
            },
            update: async (data: any, filters: Record<string, any>) => {
                return dbManager.execute({
                    action: 'update_filtered',
                    table,
                    data,
                    filters,
                    user_id: userId
                });
            },
            deleteMatch: async (filters: Record<string, any>) => {
                return dbManager.execute({
                    action: 'delete_filtered',
                    table,
                    filters,
                    user_id: userId
                });
            },
            count: async (filters?: Record<string, any>) => {
                return dbManager.execute({
                    action: 'count',
                    table,
                    filters,
                    user_id: userId
                });
            }
        };
    },

    /**
     * user_notes helpers
     */
    user_notes: {
        async delete(userId: string, id: string) {
            return dbManager.from('user_notes', userId).delete(id);
        }
    },

    userAnnotations: {
        async upsert(userId: string, bookId: string, content: string) {
            return dbManager.from('user_annotations', userId).upsert({ user_id: userId, book_id: bookId, content });
        },

        async get(userId: string, bookId: string) {
            const results = await dbManager.from('user_annotations', userId).select({ book_id: bookId });
            return results && results.length > 0 ? results[0] : null;
        }
    }
};
